/**
 * Scheda NC in 8 passi (8D leggero): D3 è l'azione nel breve, D5 quella di lungo termine (obbligatoria per chiudere),
 * D8 la verifica dell'efficacia. A destra il costo della non qualità per voce, il claim al fornitore e il fornitore.
 */
import { App, Button, Card, Col, DatePicker, Input, InputNumber, Modal, Row, Select, Space, Table, Tag, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ANCORA,
  ESITI_VERIFICA,
  FASI_NC,
  VOCI_COSTO,
  type ClaimDto,
  type CostiNcInput,
  type FaseNc,
  type ModificaNcInput,
  type NcDto,
  type VoceCosto,
} from "@qa/shared";
import { api } from "../../api/client";
import { fmtData, fmtEur, numFormatter, numParser } from "../../lib/format";
import { usePuo } from "../../lib/permessi";
import { useAggiorna } from "../../lib/query";
import { messaggioErrore } from "../../i18n/errori";
import { FornitoreSelect, OwnerInput, SelectLista } from "../../components/campi";
import { Miss, PageHead, Ro, Sev, Stato } from "../../components/ui";
import { palette } from "../../theme";

type Testi8d = "d1" | "d2" | "d3" | "d4" | "d5" | "d6" | "d7" | "d8";
type Bozza = Required<Pick<ModificaNcInput, "fase" | Testi8d | "owner" | "funzione" | "scadenza" | "fornitoreId" | "d5Owner" | "d5Scadenza" | "d5CostoStimato" | "verificaDopo" | "esitoVerifica" | "pesoRating">>;

const daNc = (n: NcDto): Bozza => ({
  fase: n.fase,
  d1: n.d1,
  d2: n.d2,
  d3: n.d3,
  d4: n.d4,
  d5: n.d5,
  d6: n.d6,
  d7: n.d7,
  d8: n.d8,
  owner: n.owner,
  funzione: n.funzione,
  scadenza: n.scadenza,
  fornitoreId: n.fornitore?.id ?? null,
  d5Owner: n.d5Owner,
  d5Scadenza: n.d5Scadenza,
  d5CostoStimato: n.d5CostoStimato,
  verificaDopo: n.verificaDopo,
  esitoVerifica: n.esitoVerifica,
  pesoRating: n.pesoRating,
});
const vuoto = (s: string | null | undefined) => (s?.trim() ? s : null);
const data = (s: string | null | undefined) => (s ? dayjs(s) : null);

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="muted small">{label}</div>
      {children}
    </div>
  );
}

function Costi({ n, ro }: { n: NcDto; ro: boolean }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const iniziale = useMemo(() => Object.fromEntries(VOCI_COSTO.map((k) => [k, n[k]])) as Record<VoceCosto, number | null>, [n]);
  const [c, setC] = useState(iniziale);
  useEffect(() => setC(iniziale), [iniziale]);
  const cambiato = VOCI_COSTO.some((k) => c[k] !== iniziale[k]);
  const totale = VOCI_COSTO.reduce((a, k) => a + (c[k] ?? 0), 0);
  const m = useMutation({
    mutationFn: () => api.put<NcDto>(`/redflag/nc/${n.id}/costi`, c satisfies CostiNcInput),
    onSuccess: () => {
      message.success(t("nc.costoSalvato"));
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Card size="small" title={t("nc.costoTitolo")} data-tip={ANCORA.ncCosti}>
      <Table
        size="small"
        showHeader={false}
        pagination={false}
        rowKey="k"
        dataSource={VOCI_COSTO.map((k) => ({ k }))}
        columns={[
          { key: "l", render: (_, { k }) => t(`dominio.voceCosto.${k}`) },
          {
            key: "v",
            align: "right",
            width: 140,
            render: (_, { k }) => (
              <InputNumber size="small" min={0} disabled={ro} value={c[k]} onChange={(v) => setC({ ...c, [k]: v ?? null })} formatter={numFormatter} parser={numParser} addonAfter="€" style={{ width: 130 }} />
            ),
          },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <b>{t("nc.costoTotale")}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <b className="num" data-sensitive>{fmtEur(totale)}</b>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      {!ro && (
        <Button size="small" type="primary" style={{ marginTop: 10 }} disabled={!cambiato} loading={m.isPending} onClick={() => m.mutate()}>
          {t("nc.costoSalva")}
        </Button>
      )}
    </Card>
  );
}

function ApriClaim({ n }: { n: NcDto }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const nav = useNavigate();
  const aggiorna = useAggiorna();
  const [open, setOpen] = useState(false);
  const [problema, setProblema] = useState(n.d2 ?? "");
  const [importo, setImporto] = useState<number | null>(n.costoTotale);
  const m = useMutation({
    mutationFn: () =>
      api.post<ClaimDto>("/claim", {
        ncId: n.id,
        fornitoreId: n.fornitore?.id ?? null,
        fornitoreTesto: n.fornitore?.nome ?? n.fornitoreTesto ?? "",
        problema: problema.trim(),
        importo,
        valuta: "EUR",
      }),
    onSuccess: (c) => {
      message.success(t("nc.claimAperto", { codice: c.codice }));
      void aggiorna();
      nav(`/claim/${c.id}`);
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const fornitore = n.fornitore?.nome ?? n.fornitoreTesto;
  return (
    <>
      <Button size="small" onClick={() => setOpen(true)} disabled={!fornitore}>
        {t("nc.apriClaim")}
      </Button>
      <Modal open={open} onCancel={() => setOpen(false)} title={t("claim.nuovo.titolo")} okText={t("nc.apriClaim")} cancelText={t("azioni.annulla")} okButtonProps={{ disabled: !problema.trim() }} confirmLoading={m.isPending} onOk={() => m.mutate()}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Campo label={t("claim.nuovo.fornitore")}>
            <b>{fornitore}</b>
          </Campo>
          <Campo label={t("claim.nuovo.problema")}>
            <Input.TextArea rows={3} maxLength={2000} value={problema} onChange={(e) => setProblema(e.target.value)} />
          </Campo>
          <Campo label={t("claim.nuovo.importo")}>
            <InputNumber min={0} value={importo} onChange={(v) => setImporto(v ?? null)} formatter={numFormatter} parser={numParser} addonAfter="€" style={{ width: "100%" }} />
          </Campo>
        </Space>
      </Modal>
    </>
  );
}

export default function NcPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const puo = usePuo();
  const aggiorna = useAggiorna();
  const q = useQuery({ queryKey: ["nc", id], queryFn: () => api.get<NcDto>(`/redflag/nc/${id}`) });
  const [b, setB] = useState<Bozza | null>(null);
  useEffect(() => {
    if (q.data) setB(daNc(q.data));
  }, [q.data]);
  const salva = useMutation({
    mutationFn: (x: Bozza) =>
      api.patch<NcDto>(`/redflag/nc/${id}`, {
        ...x,
        ...Object.fromEntries((["d1", "d2", "d3", "d4", "d5", "d6", "d7", "d8"] as const).map((k) => [k, vuoto(x[k])])),
        funzione: vuoto(x.funzione),
      } satisfies ModificaNcInput),
    onSuccess: () => {
      message.success(t("nc.salvato"));
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const chiudi = useMutation({
    mutationFn: (nota: string) => api.post<NcDto>(`/redflag/nc/${id}/chiudi`, { nota: nota.trim() || null }),
    onSuccess: () => {
      message.success(t("nc.chiusaOk"));
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const riapri = useMutation({
    mutationFn: (motivo: string) => api.post<NcDto>(`/redflag/nc/${id}/riapri`, { motivo: motivo.trim() }),
    onSuccess: () => {
      message.success(t("nc.riapertaOk"));
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const chiediTesto = (titolo: string, obbligatorio: boolean, ok: string, fn: (s: string) => Promise<unknown>) => {
    let v = "";
    modal.confirm({
      title: titolo,
      icon: null,
      okText: ok,
      cancelText: t("azioni.annulla"),
      content: <Input.TextArea rows={3} maxLength={2000} onChange={(e) => (v = e.target.value)} />,
      onOk: () => (obbligatorio && !v.trim() ? Promise.reject() : fn(v)),
    });
  };

  return (
    <Stato q={q} righe={14}>
      {(n) => {
        if (!b) return null;
        const chiusa = n.stato === "chiusa";
        const ro = chiusa || !puo("nc.lavora");
        const cambiato = JSON.stringify(b) !== JSON.stringify(daNc(n));
        const set = <K extends keyof Bozza>(k: K, v: Bozza[K]) => setB({ ...b, [k]: v });
        const testo = (k: Testi8d, f: FaseNc) => (
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 10 }} value={b[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={t(`nc.suggerimenti.${f}`)} readOnly={ro} />
        );
        const extra: Partial<Record<FaseNc, React.ReactNode>> = {
          D1: (
            <Row gutter={[10, 10]} style={{ marginTop: 8 }}>
              <Col xs={24} md={8}>
                <Campo label={t("nc.owner")}>
                  <div data-tip={ANCORA.ncOwner}>
                  <OwnerInput value={b.owner} onChange={(o) => set("owner", o)} placeholder={t("nc.ownerPh")} disabled={ro} />
                  </div>
                </Campo>
              </Col>
              <Col xs={24} md={8}>
                {n.tipo === "fornitore" ? (
                  <Campo label={t("nc.fornitore")}>
                    {puo("fornitore.vedi") ? <FornitoreSelect value={b.fornitoreId} iniziale={n.fornitore} onChange={(v) => set("fornitoreId", v)} disabled={ro} /> : <span>{n.fornitore?.nome ?? n.fornitoreTesto ?? "—"}</span>}
                  </Campo>
                ) : (
                  <Campo label={t("nc.funzione")}>
                    <SelectLista lista="funzione_nc" value={b.funzione} onChange={(v) => set("funzione", v)} />
                  </Campo>
                )}
              </Col>
              <Col xs={24} md={8}>
                <Campo label={t("nc.scadenza")}>
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" value={data(b.scadenza)} onChange={(d) => set("scadenza", d ? d.format("YYYY-MM-DD") : null)} disabled={ro} />
                </Campo>
              </Col>
            </Row>
          ),
          D5: (
            <Row gutter={[10, 10]} style={{ marginTop: 8 }}>
              <Col xs={24} md={8}>
                <Campo label={t("nc.owner")}>
                  <OwnerInput value={b.d5Owner} onChange={(o) => set("d5Owner", o)} disabled={ro} />
                </Campo>
              </Col>
              <Col xs={24} md={8}>
                <Campo label={t("nc.scadenza")}>
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" value={data(b.d5Scadenza)} onChange={(d) => set("d5Scadenza", d ? d.format("YYYY-MM-DD") : null)} disabled={ro} />
                </Campo>
              </Col>
              <Col xs={24} md={8}>
                <Campo label={t("nc.costoStimato")}>
                  <InputNumber min={0} style={{ width: "100%" }} value={b.d5CostoStimato} onChange={(v) => set("d5CostoStimato", v ?? null)} formatter={numFormatter} parser={numParser} addonAfter="€" disabled={ro} />
                </Campo>
              </Col>
            </Row>
          ),
          D8: (
            <Row gutter={[10, 10]} style={{ marginTop: 8 }}>
              <Col xs={24} md={8}>
                <Campo label={t("nc.verificaDopo")}>
                  <SelectLista lista="verifica_dopo" value={b.verificaDopo} onChange={(v) => set("verificaDopo", v)} />
                </Campo>
              </Col>
              <Col xs={24} md={8}>
                <Campo label={t("nc.esito")}>
                  <Select
                    allowClear
                    style={{ width: "100%" }}
                    value={b.esitoVerifica ?? undefined}
                    onChange={(v) => set("esitoVerifica", v ?? null)}
                    disabled={ro}
                    options={ESITI_VERIFICA.map((e) => ({ value: e, label: t(`dominio.esito.${e}`) }))}
                  />
                </Campo>
              </Col>
              <Col xs={24} md={8}>
                <Campo label={t("nc.pesoRating")}>
                  <Select
                    style={{ width: "100%" }}
                    value={b.pesoRating}
                    onChange={(v) => set("pesoRating", v)}
                    disabled={ro}
                    options={[
                      { value: true, label: t("azioni.si") },
                      { value: false, label: t("azioni.no") },
                    ]}
                  />
                </Campo>
              </Col>
            </Row>
          ),
        };
        const chiave = (f: FaseNc) => f.toLowerCase() as Testi8d;

        return (
          <>
            <PageHead
              crumbs={[
                { label: t("portale.nome"), to: "/" },
                { label: t("menu.gruppoRedflag"), to: "/redflag" },
                { label: n.segnalazione.codice, to: `/redflag/segnalazioni/${n.segnalazione.id}` },
                { label: n.codice },
              ]}
              title={`${n.codice} · ${n.segnalazione.cliente}`}
              sub={
                <>
                  {t("nc.sottotitolo", { tipo: t(`dominio.tipoNc.${n.tipo}`) })}
                  <Link to={`/redflag/segnalazioni/${n.segnalazione.id}`} className="mono">
                    {n.segnalazione.codice}
                  </Link>
                  {n.fornitore && (
                    <>
                      {" · "}
                      {t("nc.fornitoreDi")} {puo("fornitore.vedi") ? <Link to={`/fornitori/${n.fornitore.id}`}>{n.fornitore.nome}</Link> : n.fornitore.nome}
                    </>
                  )}
                </>
              }
              extra={
                <>
                  <Sev s={n.segnalazione.severita} />
                  {chiusa ? <Tag color="success">{t("nc.chiusa")}</Tag> : <Tag color="processing">{t("nc.fase", { fase: n.fase })}</Tag>}
                  {n.scaduta && !chiusa && <Tag color="error">{t("nc.scaduta")}</Tag>}
                  {puo("nc.lavora") &&
                    (chiusa ? (
                      <Button onClick={() => chiediTesto(t("nc.riapriMotivo"), true, t("nc.riapri"), (s) => riapri.mutateAsync(s))}>{t("nc.riapri")}</Button>
                    ) : (
                      <Tooltip title={n.chiudibile ? undefined : t("nc.chiudiNonPronta")}>
                        <Button data-tip={ANCORA.ncChiudi} disabled={!n.chiudibile || cambiato} onClick={() => chiediTesto(t("nc.chiudiNota"), false, t("nc.chiudi"), (s) => chiudi.mutateAsync(s))}>
                          {t("nc.chiudi")}
                        </Button>
                      </Tooltip>
                    ))}
                </>
              }
            />
            {chiusa && <p className="muted">{t("nc.chiusaIl", { data: fmtData(n.chiusaIl) })}</p>}
            <Row gutter={[16, 16]}>
              <Col xs={24} xl={16}>
                {!ro && (
                  <Space wrap style={{ marginBottom: 10 }}>
                    <span className="muted">{t("nc.faseCorrente")}</span>
                    <Select value={b.fase} onChange={(v) => set("fase", v)} style={{ width: 280, maxWidth: "100%" }} options={FASI_NC.map((f) => ({ value: f, label: `${f} · ${t(`dominio.faseNc.${f}`)}` }))} />
                  </Space>
                )}
                <div className="d8" style={{ display: "grid", gap: 10 }}>
                  {FASI_NC.map((f) => {
                    const pieno = !!b[chiave(f)]?.trim();
                    return (
                      <Card
                        key={f}
                        size="small"
                        style={f === b.fase && !chiusa ? { borderColor: palette.primary } : undefined}
                        title={
                          <>
                            <span className="d8n">{f}</span>
                            {t(`dominio.faseNc.${f}`)}
                          </>
                        }
                        extra={<Tag bordered={false} color={pieno ? "success" : "default"}>{pieno ? t("nc.fatto") : t("nc.daFare")}</Tag>}
                      >
                        {testo(chiave(f), f)}
                        {extra[f]}
                      </Card>
                    );
                  })}
                </div>
                {!ro && (
                  <div className="rs-savebar" style={{ marginTop: 12 }}>
                    {cambiato && <span className="muted">{t("comune.modificheNonSalvate")}</span>}
                    <Button onClick={() => setB(daNc(n))} disabled={!cambiato}>
                      {t("azioni.annulla")}
                    </Button>
                    <Button type="primary" disabled={!cambiato} loading={salva.isPending} onClick={() => salva.mutate(b)}>
                      {t("nc.salva8d")}
                    </Button>
                  </div>
                )}
              </Col>
              <Col xs={24} xl={8}>
                {puo("costi.vedi") && <Costi n={n} ro={chiusa || !puo("nc.costi")} />}
                {n.tipo === "fornitore" && puo("claim.gestisci") && (
                  <Card size="small" style={{ marginTop: 16 }}>
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                      <b>{t("nc.recuperabile")}</b>
                      <ApriClaim n={n} />
                    </Space>
                  </Card>
                )}
                {(n.fornitore || n.fornitoreTesto) && (
                  <Card
                    size="small"
                    style={{ marginTop: 16 }}
                    title={
                      <>
                        {t("nc.fornitoreTitolo")} <Ro>{t("comune.vendorhub")}</Ro>
                      </>
                    }
                  >
                    <p style={{ margin: "0 0 8px" }}>
                      <b>{n.fornitore && puo("fornitore.vedi") ? <Link to={`/fornitori/${n.fornitore.id}`}>{n.fornitore.nome}</Link> : (n.fornitore?.nome ?? n.fornitoreTesto)}</b>
                    </p>
                    <dl className="kv stretto">
                      <dt>{t("nc.rating")}</dt>
                      <dd className="muted">{t("nc.ratingDa")}</dd>
                      <dt>{t("nc.ncAudit")}</dt>
                      <dd className="muted">{t("nc.ncAuditDa")}</dd>
                      <dt>{t("nc.altriCasi")}</dt>
                      <dd>{n.altriCasiFornitore ? t("nc.altriCasiValore", { nc: n.altriCasiFornitore.nc, claim: n.altriCasiFornitore.claim }) : <Miss>—</Miss>}</dd>
                    </dl>
                  </Card>
                )}
                <Card size="small" title={t("nc.allegati")} style={{ marginTop: 16 }}>
                  <div className="prev">{t("nc.allegatiInArrivo")}</div>
                </Card>
              </Col>
            </Row>
          </>
        );
      }}
    </Stato>
  );
}
